// Reusable UI primitives: Stepper, Segmented, FeelControl, Toast, Sheet, AutoTextarea.
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode, type CSSProperties } from 'react';
import type { Feel } from '../domain/types';
import { IconPlus, IconMinus } from './icons';

// Numeric field with +/- steppers AND direct type-in. The +/- buttons jump by
// `step` (each exercise's natural increment); tapping the number lets you type the
// exact load instead of clicking many times. While focused, an internal string
// holds the raw text so partial input ("2.", "") doesn't fight the user; on blur it
// snaps back to the formatted committed value.
export function Stepper({
  value, onChange, step, min = 0, unit, decimals = 1,
}: {
  value: number;
  onChange: (v: number) => void;
  step: number;
  min?: number;
  unit?: string;
  decimals?: number;
}) {
  const fmt = (n: number) => {
    const r = Math.round(n * 100) / 100;
    return Number.isInteger(r) ? String(r) : r.toFixed(decimals).replace(/\.0$/, '');
  };
  const [editing, setEditing] = useState<string | null>(null);
  const display = editing ?? fmt(value);
  return (
    <div className="stepper" role="group">
      <button type="button" aria-label="decrease" onClick={() => onChange(Math.max(min, Math.round((value - step) * 100) / 100))}>
        <IconMinus width={20} height={20} />
      </button>
      <div className="val">
        <input
          className="num"
          type="text"
          inputMode="decimal"
          value={display}
          aria-label={unit ? `value (${unit})` : 'value'}
          style={{ background: 'transparent', border: 'none', textAlign: 'center', width: '2.6em', fontFamily: 'inherit', color: 'inherit', padding: 0 }}
          onFocus={(e) => { setEditing(fmt(value)); e.currentTarget.select(); }}
          onChange={(e) => {
            const raw = e.target.value;
            setEditing(raw);
            const n = parseFloat(raw);
            if (!Number.isNaN(n)) onChange(Math.max(min, Math.round(n * 100) / 100));
          }}
          onBlur={() => setEditing(null)}
        />
        {unit && <span className="unit">{unit}</span>}
      </div>
      <button type="button" aria-label="increase" onClick={() => onChange(Math.round((value + step) * 100) / 100)}>
        <IconPlus width={20} height={20} />
      </button>
    </div>
  );
}

export function Segmented<T extends string | number>({
  options, value, onChange,
}: {
  options: { label: string; value: T }[];
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <div className="segmented">
      {options.map((o) => (
        <button
          key={String(o.value)}
          type="button"
          className={o.value === value ? 'on' : ''}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

const FEEL_OPTS: { label: string; value: Feel }[] = [
  { label: 'None', value: 'none' },
  { label: 'Aware', value: 'aware' },
  { label: 'Pain', value: 'pain' },
];

export function FeelControl({
  label, value, onChange,
}: {
  label: string;
  value: Feel | null;
  onChange: (v: Feel) => void;
}) {
  return (
    <div className="field" style={{ marginBottom: 8 }}>
      <label>{label}</label>
      <div className="segmented feel">
        {FEEL_OPTS.map((o) => (
          <button
            key={o.value}
            type="button"
            data-feel={o.value}
            className={o.value === value ? 'on' : ''}
            onClick={() => onChange(o.value)}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// A note field that grows with its content instead of clipping / forcing the user
// to scroll inside a fixed box. Starts at one line and expands to fit; no manual
// resize handle. Re-measures on every value change so it also shrinks on delete.
export function AutoTextarea({
  value, onChange, placeholder, className = 'input', style, minRows = 1,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  className?: string;
  style?: CSSProperties;
  minRows?: number;
}) {
  const ref = useRef<HTMLTextAreaElement | null>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [value]);
  return (
    <textarea
      ref={ref}
      className={className}
      placeholder={placeholder}
      value={value}
      rows={minRows}
      onChange={(e) => onChange(e.target.value)}
      style={{ resize: 'none', overflow: 'hidden', minHeight: 0, ...style }}
    />
  );
}

export function Toast({ message, onDone, ms = 1800 }: { message: string; onDone: () => void; ms?: number }) {
  useEffect(() => {
    const t = setTimeout(onDone, ms);
    return () => clearTimeout(t);
  }, [message, ms, onDone]);
  return <div className="toast">{message}</div>;
}

export function Sheet({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <div className="sheet" onClick={(e) => e.stopPropagation()}>
        <div className="spread" style={{ marginBottom: 12 }}>
          <strong>{title}</strong>
          <button className="btn ghost sm" onClick={onClose}>Close</button>
        </div>
        {children}
      </div>
    </div>
  );
}
